import { createTheme, type Theme } from '@mui/material/styles';

export type ResolvedThemeMode = 'light' | 'dark';

const sharedTypography = {
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  h1: { fontSize: '2.5rem', fontWeight: 500 },
  h2: { fontSize: '2rem', fontWeight: 500 },
  h3: { fontSize: '1.75rem', fontWeight: 500 },
  h4: { fontSize: '1.5rem', fontWeight: 500 },
  h5: { fontSize: '1.25rem', fontWeight: 500 },
  h6: { fontSize: '1rem', fontWeight: 500 },
  body1: { fontSize: '1rem' },
  body2: { fontSize: '0.875rem' },
} as const;

const sharedComponents = {
  MuiTypography: {
    styleOverrides: {
      h1: { '@media (max-width:600px)': { fontSize: '2rem' } },
      h2: { '@media (max-width:600px)': { fontSize: '1.75rem' } },
      h3: { '@media (max-width:600px)': { fontSize: '1.5rem' } },
      h4: { '@media (max-width:600px)': { fontSize: '1.25rem' } },
      h5: { '@media (max-width:600px)': { fontSize: '1.1rem' } },
      h6: { '@media (max-width:600px)': { fontSize: '0.9rem' } },
      body1: { '@media (max-width:600px)': { fontSize: '0.9rem' } },
      body2: { '@media (max-width:600px)': { fontSize: '0.8rem' } },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none' as const,
        borderRadius: 8,
        padding: '8px 16px',
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: { borderRadius: 12 },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: { borderRadius: 12 },
    },
  },
};

export function createAppTheme(mode: ResolvedThemeMode): Theme {
  return createTheme({
    cssVariables: true,
    palette: {
      mode,
      primary: {
        main: '#4a90e2',
        light: '#7bbfff',
        dark: '#0064b0',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#5c6bc0',
        light: '#8e99f3',
        dark: '#26418f',
        contrastText: '#ffffff',
      },
      error: { main: '#f44336' },
      warning: { main: '#ff9800' },
      info: { main: '#2196f3' },
      success: { main: '#4caf50' },
      ...(mode === 'light'
        ? {
            background: { default: '#f5f5f5', paper: '#ffffff' },
            text: { primary: '#333333', secondary: '#666666' },
          }
        : {
            background: { default: '#121212', paper: '#1e1e1e' },
            text: { primary: '#f5f5f5', secondary: '#b0b0b0' },
          }),
    },
    typography: sharedTypography,
    shape: { borderRadius: 8 },
    components: sharedComponents,
  });
}
